import { getDonationStep, getDonationSteps } from '@/utils/donationStepUtils';
import { deleteRequest, ENDPOINTS } from '@/api/mainApi';
import { DonationStep } from '@/interfaces/donationStep';
import { trackEvent } from '@/utils/pageSense';
import { DonationStepFlowVersion } from '@/enums/donationStepFlowVersion';

/**
 * @description Mixin for handling stepper flow in donation process.
 * Should be used with the Donation step flow components, e.g. DonationStepFlowV1Component.
 */
export default {
  name: 'StepperFlowMixin',
  data() {
    return {
      // todo - should this be put in store?
      donationStepFlowVersion: DonationStepFlowVersion.V1,
    }
  },
  methods: {
    previousStep(this: any) {
      const currentStepNumber: number = this.currentStep.step;
      const previousSteps = this.availableSteps
        .map((step: DonationStep) => step.step)
        .filter((stepNumber: number) => stepNumber < currentStepNumber)
        .sort((a: number, b: number) => b - a);

      if (previousSteps.length > 0) {
        this.$store.commit('updateCurrentStep', previousSteps[0]);
        this.$nextTick(() => {
          const previousStep = this.availableSteps.find((val: DonationStep) => val.step === previousSteps[0]);
          trackEvent('Navigated to previous step: ' + (previousStep ? previousStep.title : previousSteps[0]));
        });
      }
    },

    nextStep(this: any) {
      const currentStepNumber: number = this.currentStep.step;
      const nextSteps = this.availableSteps
        .map((step: DonationStep) => step.step)
        .filter((stepNumber: number) => stepNumber > currentStepNumber)
        .sort((a: number, b: number) => a - b);

      if (nextSteps.length > 0) {
        this.$nextTick(() => {
          const nextStep = this.availableSteps.find((val: DonationStep) => val.step === nextSteps[0]);
          trackEvent('Navigated to next step: ' + (nextStep ? nextStep.title : nextSteps[0]));
        });
        this.$store.commit('updateCurrentStep', nextSteps[0]);
      }
    },

    removeStep(this: any, stepToRemove: number) {
      const index = this.availableSteps.findIndex((val: DonationStep) => val.step === stepToRemove);
      if (index >= 0) {
        this.availableSteps.splice(index, 1);
      }
    },

    addStep(this: any, stepToAdd: number) {
      let guideStep = this.availableSteps.find((val: DonationStep) => val.step === stepToAdd);
      if (!guideStep) {
        guideStep = this.getGuideStep(stepToAdd);
        if (guideStep) {
          this.availableSteps.push(guideStep);
          this.availableSteps.sort((a, b) => a.step - b.step);
        }
      }
    },

    setStep(this: any, step: number) {
      this.$store.commit('updateCurrentStep', step);
      this.$nextTick(() => {
        const newStep = this.availableSteps.find((val: DonationStep) => val.step === step);
        trackEvent('Navigated to next step: ' + (newStep ? newStep.title : step));
      });
    },

    async reset(this: any) {
      this.loading = true;

      if (this.isDigitalPayment()) {
        await deleteRequest(`${ENDPOINTS.PAYMENTS}/${ENDPOINTS.TRANSACTIONS}`, {
          donationId: this.donation.id
        });
      } else {
        await deleteRequest(`${ENDPOINTS.INVOICE}/${ENDPOINTS.FINALIZE}`, {
          donationId: this.donation.id
        });
        const resetDonation = { ...this.donation, id: null };
        this.$store.commit('updateDonation', resetDonation);
      }

      this.setStep(this.availableSteps[1].step);
      this.loading = false;
      this.$nextTick(() => {
        trackEvent('DonationModule was reset');
      });
    },

    getDonationStep(this: any, stepNumber: number): DonationStep | undefined {
      return getDonationStep(stepNumber, this.donationStepFlowVersion);
    },

    getInitialSteps(this: any): DonationStep[] {
      return [...getDonationSteps(this.donationStepFlowVersion)];
    }
  }
}
